import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  getTiles,
  deleteTile,
  editTile,
  getSizes,
  toAssetUrl,
  generateProductQR,
  downloadProductQR,
  filterBySize,
  getProductStockHistory,
} from "../../services/api";
import RetailerSidebar from "./RetailerSidebar";
import AddTileModal from "./AddTiles";
import colors from "../../theme/colors";
import { StockHistoryModal } from "../retailer/stock/StockHistoryModal";
import EditTileModal from "./tiles/EditTileModal";

export default function Tiles() {
  const navigate = useNavigate();
  const location = useLocation();
  const [tiles, setTiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [editModal, setEditModal] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [editFormData, setEditFormData] = useState({
    product_size_id: "",
    application_type: "both",
    surface_type: "",
    price: "",
    sku: "",
    pieces_per_box: "",
    price_unit: "Box",
    coverage_area_per_box: "",
    coverage_area_unit: "Per Sqmr",
    weight_per_box: "",
    weight_unit: "Kg",
    thickness_mm: "",
    images: [],
  });
  const [sizes, setSizes] = useState([]);
  const [editLoading, setEditLoading] = useState(false);
  const [selectedTiles, setSelectedTiles] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedSizeFilter, setSelectedSizeFilter] = useState("all");
  const [actionMenuOpen, setActionMenuOpen] = useState(null);
  const [sizeFilterFromNav, setSizeFilterFromNav] = useState(null);
  const [addModalOpen, setAddModalOpen] = useState(false);

  const [stockHistoryModal, setStockHistoryModal] = useState({
  isOpen: false,
  productId: null,
  productName: "",
  data: [],
  loading: false,
  error: null,
});

const handleOpenStockHistoryModal = async (tile) => {
  setStockHistoryModal({
    isOpen: true,
    productId: tile.id,
    productName: tile.name || tile.product_name || "",
    data: [],
    loading: true,
    error: null,
  });

  try {
    const res = await getProductStockHistory(tile.id);
    setStockHistoryModal((prev) => ({
      ...prev,
      data: res.data || [],
      loading: false,
    }));
  } catch (err) {
    setStockHistoryModal((prev) => ({
      ...prev,
      error: err.message || "Failed to load stock history",
      loading: false,
    }));
  }
};

const handleCloseStockHistoryModal = () => {
    setStockHistoryModal((prev) => ({ ...prev, isOpen: false }));
  };

  // Filter tiles based on search query (size filtering is now done via API)
  const filteredTiles = tiles.filter((tile) => {
    const matchesSearch =
      !searchQuery ||
      tile.sku?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      tile.surface_type?.toLowerCase().includes(searchQuery.toLowerCase());

    return matchesSearch;
  });

  const [previewModalTile, setPreviewModalTile] = useState(null);

  const handleOpenPreviewModal = (tile) => {
    setPreviewModalTile(tile);
    setActionMenuOpen(null);
  };

  const handleCreateInVisualizer = (tile) => {
    // Add logic to open visualizer or navigate
    console.log("Create in visualizer for:", tile);
    setPreviewModalTile(null);
  };

  const handleToggleStatus = () => {};

  const handleUploadPreviewImage = (tile) => {
    // Add logic to upload image
    console.log("Upload image for:", tile);
    setPreviewModalTile(null);
  };

  // Paginate tiles
  const paginatedTiles = filteredTiles.slice(
    (currentPage - 1) * entriesPerPage,
    currentPage * entriesPerPage,
  );
  // console.log("paginatedTiles: ", paginatedTiles)

  const getImageSrc = (image) => toAssetUrl(image?.url || image);

  // Handle size filter from navigation
  useEffect(() => {
    if (location.state?.sizeFilter) {
      setSelectedSizeFilter(location.state.sizeFilter);
      setSizeFilterFromNav(location.state.sizeFilter);
    }
  }, [location.state]);

  useEffect(() => {
    fetchTiles();
    fetchSizes();
  }, []);

  const [qrLoadingId, setQrLoadingId] = useState(null);

  const handleGenerateQR = async (tileId) => {
    setActionMenuOpen(null);
    const token = localStorage.getItem("retailer_token"); // Retrieve your saved token

    try {
      setQrLoadingId(tileId);
      const data = await generateProductQR(tileId, token);
      alert("QR Code generated successfully!");
      // Optionally trigger a refetch of your tiles list here if needed
    } catch (err) {
      console.error(err);
      alert(err.message || "Failed to generate QR code");
    } finally {
      setQrLoadingId(null);
    }
  };

  const handleDownloadQR = async (tileId) => {
    setActionMenuOpen(null);
    const token = localStorage.getItem("retailer_token"); // Retrieve your saved token

    try {
      setQrLoadingId(tileId);
      await downloadProductQR(tileId, token);
    } catch (err) {
      console.error(err);
      alert(err.message || "Failed to download QR code");
    } finally {
      setQrLoadingId(null);
    }
  };

  const fetchTiles = async () => {
    const token = localStorage.getItem("retailer_token");
    
    try {
      const response = await getTiles();
      if (response.success) {
        setTiles(response.products);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchSizes = async () => {
    const token = localStorage.getItem("retailer_token");
    
    try {
      const response = await getSizes();
      if (response.success) {
        setSizes(response.sizes);
      }
    } catch (err) {
      console.error("Failed to fetch sizes:", err);
    }
  };

  const handleDelete = async (tileId) => {
    try {
      const token = localStorage.getItem("retailer_token");
      const response = await deleteTile(tileId, token);
      if (response.success) {
        setTiles(tiles.filter((t) => t.id !== tileId));
        setDeleteConfirm(null);
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleEdit = (tile) => {
    setEditFormData({
      product_size_id: tile.product_size_id || "",
      application_type: tile.application_type || "both",
      surface_type: tile.surface_type || "",
      price: tile.price || "",
      sku: tile.sku || "",
      pieces_per_box: tile.pieces_per_box || "",
      price_unit: tile.price_unit || "Box",
      coverage_area_per_box: tile.coverage_area_per_box || "",
      coverage_area_unit: tile.coverage_area_unit || "Per Sqmr",
      weight_per_box: tile.weight_per_box || "",
      weight_unit: tile.weight_unit || "Kg",
      thickness_mm: tile.thickness_mm || "",
      images: [],
    });
    setEditModal(tile);
  };

  const handleEditChange = (e) => {
    const { name, value, files } = e.target;
    setEditFormData((prev) => ({
      ...prev,
      [name]: name === "images" ? files : value,
    }));
  };

  const handleEditSubmit = async (formDataToSend) => {
    setEditLoading(true);
    setError("");

    const token = localStorage.getItem("retailer_token");

    try {
      const response = await editTile(
        editModal.id,
        formDataToSend,
        token
      );

      if (response) {
        setEditModal(null);
        fetchTiles();
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setEditLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <RetailerSidebar />

      {/* Main Content */}
      <main className="flex-1 ml-64 p-6 w-full">
        <div className="w-full">
          {/* Product Filters Section */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4 shadow-sm">
            <div className="flex items-center gap-4">
              <label
                className="text-sm font-medium"
                style={{ color: colors.t2 }}
              >
                Select Product Size:
              </label>
              <select
                value={selectedSizeFilter}
                onChange={async (e) => {
                  const newSize = e.target.value;
                  setSelectedSizeFilter(newSize);
                  setCurrentPage(1);
                  
                  const token = localStorage.getItem("retailer_token");
                  
                  
                  try {
                    setLoading(true);
                    const sizeValue = newSize === "all" ? null : newSize;
                    const response = await filterBySize({ 
                      size: sizeValue, 
                      page: 1, 
                      limit: 100, 
                      token 
                    });
                    if (response.success) {
                      setTiles(response.products);
                    }
                  } catch (err) {
                    console.error("Failed to filter by size:", err);
                    setError(err.message);
                  } finally {
                    setLoading(false);
                  }
                }}
                className="border border-gray-300 rounded-md p-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent"
              >
                <option value="all">All Product Size</option>
                {sizes.map((size) => (
                  <option key={size.id} value={size.name}>
                    {size.name} ({size.width}x{size.height} {size.unit})
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Product Design SKU Section */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
            {/* Header */}
            <div className="p-4 border-b border-gray-200 flex justify-between items-center flex-wrap gap-4">
              <div className="flex gap-3">
                <button
                  onClick={() => navigate("/bulk-upload")}
                  className="bg-accent hover:bg-accent2 text-white font-medium py-2 px-4 rounded-lg transition-colors text-sm"
                >
                  Bulk Product Upload & Export
                </button>
                <button
                  onClick={() => setAddModalOpen(true)}
                  className="bg-accent hover:bg-accent2 text-white font-medium py-2 px-4 rounded-lg transition-colors text-sm"
                >
                  Add New Product Design
                </button>
              </div>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search Product Design SKU..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="border border-gray-300 rounded-md p-2 pl-10 text-sm focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent w-64"
                />
                <svg
                  className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
            </div>

            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                {error}
              </div>
            )}

            {loading ? (
              <div className="text-center py-12">
                <p className="text-gray-500">Loading tiles...</p>
              </div>
            ) : tiles.length === 0 ? (
              <div className="bg-white rounded-xl border border-gray-200 p-12 shadow-sm text-center">
                <p className="text-gray-500 mb-4">No tiles added yet</p>
                <button
                  onClick={() => setAddModalOpen(true)}
                  className="bg-accent hover:bg-accent2 text-white font-semibold py-3 px-6 rounded-lg transition-all"
                >
                  Add Your First Tile
                </button>
              </div>
            ) : (
              <>
                {/* Table Header with Show entries */}
                <div className="p-4 border-b border-gray-200">
                  <div className="flex items-center">
                    <span className="text-sm mr-2" style={{ color: colors.t2 }}>
                      Show
                    </span>
                    <select
                      value={entriesPerPage}
                      onChange={(e) => {
                        setEntriesPerPage(Number(e.target.value));
                        setCurrentPage(1);
                      }}
                      className="border border-gray-300 rounded-md p-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent"
                    >
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                    <span className="text-sm ml-2" style={{ color: colors.t2 }}>
                      entries
                    </span>
                  </div>
                </div>

                {/* Table */}
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-4 py-3 text-center">
                          <input
                            type="checkbox"
                            checked={selectAll}
                            onChange={(e) => {
                              setSelectAll(e.target.checked);
                              if (e.target.checked) {
                                setSelectedTiles(
                                  filteredTiles.map((t) => t.id),
                                );
                              } else {
                                setSelectedTiles([]);
                              }
                            }}
                            className="w-4 h-4 text-accent border-gray-300 rounded focus:ring-accent"
                          />
                        </th>
                        <th
                          className="text-left px-4 py-3 text-sm font-semibold"
                          style={{ color: colors.t2 }}
                        >
                          No.
                        </th>
                        <th
                          className="text-left px-4 py-3 text-sm font-semibold"
                          style={{ color: colors.t2 }}
                        >
                          Image
                        </th>
                        <th
                          className="text-left px-4 py-3 text-sm font-semibold"
                          style={{ color: colors.t2 }}
                        >
                          Product Details
                        </th>
                        <th
                          className="text-left px-4 py-3 text-sm font-semibold"
                          style={{ color: colors.t2 }}
                        >
                          Quick Actions
                        </th>
                        <th
                          className="text-left px-4 py-3 text-sm font-semibold"
                          style={{ color: colors.t2 }}
                        >
                          QR Code
                        </th>
                        <th
                          className="text-left px-4 py-3 text-sm font-semibold"
                          style={{ color: colors.t2 }}
                        >
                          Action
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedTiles.map((tile, index) => {
                        const hasQrCode = Boolean(
                          tile.qr_code_url || tile.qr_code || tile.has_qr,
                        );

                        return (
                          <tr
                            key={tile.id}
                            className="border-b border-gray-200 hover:bg-gray-50"
                          >
                            <td className="px-4 py-3 text-center">
                              <input
                                type="checkbox"
                                checked={selectedTiles.includes(tile.id)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedTiles([
                                      ...selectedTiles,
                                      tile.id,
                                    ]);
                                  } else {
                                    setSelectedTiles(
                                      selectedTiles.filter(
                                        (id) => id !== tile.id,
                                      ),
                                    );
                                  }
                                  setSelectAll(false);
                                }}
                                className="w-4 h-4 text-accent border-gray-300 rounded focus:ring-accent"
                              />
                            </td>
                            <td
                              className="px-4 py-3 text-sm"
                              style={{ color: colors.t3 }}
                            >
                              {(currentPage - 1) * entriesPerPage + index + 1}
                            </td>
                            <td className="px-4 py-3">
                              {tile.images && tile.images.length > 0 ? (
                                <>
                                  <img
                                    src={getImageSrc(tile.images[0])}
                                    alt={tile.sku || "Tile"}
                                    onClick={() =>
                                      setSelectedImage(
                                        getImageSrc(tile.images[0]),
                                      )
                                    }
                                    className="w-24 h-24 object-cover rounded border border-gray-200 shadow-lg cursor-pointer hover:opacity-85 transition-opacity"
                                  />

                                  {/* Transparent Light-box Pop-up */}
                                  {selectedImage && (
                                    <div
                                      className="fixed inset-0 z-50 backdrop-blur-[2px] flex items-center justify-center p-4"
                                      onClick={() => setSelectedImage(null)}
                                    >
                                      <div
                                        className="relative bg-white rounded-2xl shadow-xl border border-gray-200 p-3 max-w-lg w-full"
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        {/* Close Button */}
                                        <button
                                          type="button"
                                          onClick={() => setSelectedImage(null)}
                                          className="absolute -top-2.5 -right-2.5 z-10 w-7 h-7 bg-white text-gray-700 hover:text-black rounded-full flex items-center justify-center text-xs font-bold shadow-md border border-gray-200 transition-transform hover:scale-105"
                                        >
                                          ✕
                                        </button>

                                        {/* Image Preview Box */}
                                        <div className="w-full bg-gray-50 rounded-xl overflow-hidden flex items-center justify-center max-h-[70vh]">
                                          <img
                                            src={selectedImage}
                                            alt="Tile Preview"
                                            className="w-full h-auto max-h-[68vh] object-contain rounded-lg"
                                          />
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </>
                              ) : (
                                <div className="w-12 h-12 bg-gray-100 rounded border border-gray-200 flex items-center justify-center">
                                  <span className="text-gray-400 text-xs">
                                    No img
                                  </span>
                                </div>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              <div>
                                <div
                                  className="font-medium text-sm"
                                  style={{ color: colors.t1 }}
                                >
                                  Name: {tile.sku || "-"}
                                </div>
                                <div
                                  className="text-xs"
                                  style={{ color: colors.t3 }}
                                >
                                  Size: {tile.product_size || "-"}
                                </div>
                                <div
                                  className="text-xs"
                                  style={{ color: colors.t3 }}
                                >
                                  Type: {tile.application_type || "-"}
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <span className="text-xs font-semibold text-gray-700">
                                  Status:
                                </span>
                                <button
                                  type="button"
                                  onClick={() => handleToggleStatus(tile.id)}
                                  className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                                    tile.status === "active" ||
                                    tile.status === true ||
                                    !tile.status
                                      ? "bg-emerald-500"
                                      : "bg-gray-300"
                                  }`}
                                >
                                  <span
                                    className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out ${
                                      tile.status === "active" ||
                                      tile.status === true ||
                                      !tile.status
                                        ? "translate-x-4"
                                        : "translate-x-0"
                                    }`}
                                  />
                                </button>
                              </div>
                            </td>

                            {/* DEDICATED QR CODE COLUMN */}
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() => handleGenerateQR(tile.id)}
                                  disabled={
                                    hasQrCode || qrLoadingId === tile.id
                                  }
                                  className="px-2.5 py-1.5 text-xs font-semibold text-indigo-700 bg-indigo-50 border border-indigo-200 hover:bg-indigo-100 rounded-md transition-colors disabled:opacity-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:border-gray-200 disabled:cursor-not-allowed"
                                >
                                  {qrLoadingId === tile.id
                                    ? "Generating..."
                                    : "Generate"}
                                </button>

                                <button
                                  type="button"
                                  onClick={() => handleDownloadQR(tile.id)}
                                  disabled={
                                    !hasQrCode || qrLoadingId === tile.id
                                  }
                                  className="px-2.5 py-1.5 text-xs font-semibold text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-md transition-colors disabled:opacity-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:border-gray-200 disabled:cursor-not-allowed"
                                >
                                  {qrLoadingId === tile.id
                                    ? "Downloading..."
                                    : "Download"}
                                </button>
                              </div>
                            </td>

                            {/* ACTIONS MENU */}
                            <td className="px-4 py-3">
                              <div className="relative">
                                <button
                                  onClick={() =>
                                    setActionMenuOpen(
                                      actionMenuOpen === tile.id
                                        ? null
                                        : tile.id,
                                    )
                                  }
                                  className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                                >
                                  <svg
                                    className="w-5 h-5 text-indigo-600 hover:text-indigo-800 transition-colors"
                                    fill="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <circle cx="5" cy="12" r="2" />
                                    <circle cx="12" cy="12" r="2" />
                                    <circle cx="19" cy="12" r="2" />
                                  </svg>
                                </button>
                                {actionMenuOpen === tile.id && (
  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-10">
    <button
      onClick={() => {
        handleEdit(tile);
        setActionMenuOpen(null);
      }}
      className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition-colors"
      style={{ color: colors.t2 }}
    >
      Edit Product
    </button>
    <button
      onClick={() => {
        handleOpenPreviewModal(tile);
        setActionMenuOpen(null);
      }}
      className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition-colors"
      style={{ color: colors?.t2 || "#4B5563" }}
    >
      Show Product Preview
    </button>

    <button
      onClick={() => {
        handleOpenStockHistoryModal(tile);
        setActionMenuOpen(null);
      }}
      className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition-colors flex items-center gap-2"
      style={{ color: colors?.t2 || "#4B5563" }}
    >
      Stock History
    </button>

    <button
      onClick={() => {
        setDeleteConfirm(tile.id);
        setActionMenuOpen(null);
      }}
      className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
    >
      Delete Product
    </button>
  </div>
)}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div className="p-4 border-t border-gray-200 flex justify-between items-center">
                  <div className="text-sm" style={{ color: colors.t2 }}>
                    Showing{" "}
                    {filteredTiles.length > 0
                      ? (currentPage - 1) * entriesPerPage + 1
                      : 0}{" "}
                    to{" "}
                    {Math.min(
                      currentPage * entriesPerPage,
                      filteredTiles.length,
                    )}{" "}
                    of {filteredTiles.length} entries
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() =>
                        setCurrentPage(Math.max(1, currentPage - 1))
                      }
                      disabled={currentPage === 1}
                      className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                      style={{ color: colors.t2 }}
                    >
                      Previous
                    </button>
                    {Array.from(
                      {
                        length: Math.ceil(
                          filteredTiles.length / entriesPerPage,
                        ),
                      },
                      (_, i) => i + 1,
                    ).map((page) => (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`px-3 py-1 border rounded text-sm transition-colors ${
                          currentPage === page
                            ? "text-white"
                            : "hover:bg-gray-50"
                        }`}
                        style={{
                          backgroundColor:
                            currentPage === page ? colors.accent : "",
                          color: currentPage === page ? "#fff" : colors.t2,
                          borderColor:
                            currentPage === page ? colors.accent : "#d1d5db",
                        }}
                      >
                        {page}
                      </button>
                    ))}
                    <button
                      onClick={() =>
                        setCurrentPage(
                          Math.min(
                            Math.ceil(filteredTiles.length / entriesPerPage),
                            currentPage + 1,
                          ),
                        )
                      }
                      disabled={
                        currentPage ===
                          Math.ceil(filteredTiles.length / entriesPerPage) ||
                        filteredTiles.length === 0
                      }
                      className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                      style={{ color: colors.t2 }}
                    >
                      Next
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </main>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Confirm Delete
            </h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete this tile? This action cannot be
              undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      <EditTileModal
  isOpen={Boolean(editModal)}
  tileData={editModal}
  sizes={sizes}
  onClose={() => setEditModal(null)}
  onSave={handleEditSubmit}
  isLoading={editLoading}
/>

      {/* Create Product Preview Modal */}
      {previewModalTile && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full shadow-2xl overflow-hidden font-sans border border-gray-100">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-100 flex items-start justify-between">
              <div>
                <h3 className="text-lg font-bold text-gray-800">
                  Create Product Preview
                </h3>
                <p className="text-xs text-gray-400 mt-0.5 font-mono">
                  {previewModalTile.sku || previewModalTile.id}
                </p>
              </div>
              <button
                onClick={() => setPreviewModalTile(null)}
                className="text-gray-400 hover:text-gray-600 transition-colors pt-1"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              <p className="text-sm text-gray-500 mb-5">
                Choose how you want to create the product preview:
              </p>

              <div className="grid grid-cols-2 gap-4">
                {/* Option 1: Create in Visualizer */}
                <button
                  type="button"
                  onClick={() => handleCreateInVisualizer(previewModalTile)}
                  className="flex flex-col items-center justify-center p-6 border-2 border-gray-800 rounded-xl hover:bg-gray-50 transition-all text-center group"
                >
                  <div className="w-10 h-10 mb-3 flex items-center justify-center text-gray-800">
                    <svg
                      className="w-8 h-8"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.8}
                        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15.5 8.5l2 2"
                      />
                    </svg>
                  </div>
                  <span className="text-sm font-semibold text-gray-900 block mb-1">
                    Create in Visualizer
                  </span>
                  <span className="text-xs text-gray-500 leading-tight">
                    Apply this product on a room
                  </span>
                </button>

                {/* Option 2: Upload Image */}
                <button
                  type="button"
                  onClick={() => handleUploadPreviewImage(previewModalTile)}
                  className="flex flex-col items-center justify-center p-6 border-2 border-gray-800 rounded-xl hover:bg-gray-50 transition-all text-center group"
                >
                  <div className="w-10 h-10 mb-3 flex items-center justify-center text-gray-800">
                    <svg
                      className="w-8 h-8"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.8}
                        d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                      />
                    </svg>
                  </div>
                  <span className="text-sm font-semibold text-gray-900 block mb-1">
                    Upload Image
                  </span>
                  <span className="text-xs text-gray-500 leading-tight">
                    Upload a ready preview image
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Tile Modal */}
      <AddTileModal
        isOpen={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onSuccess={() => fetchTiles()}
      />

      <StockHistoryModal
        isOpen={stockHistoryModal.isOpen}
        onClose={handleCloseStockHistoryModal}
        historyData={stockHistoryModal.data}
        loading={stockHistoryModal.loading}
        error={stockHistoryModal.error}
        productName={stockHistoryModal.productName}
      />
    </div>
  );
}
